/* ------------------------ Funtional imports start ------------------------- */
import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { DetailResponse, type PaginatedResponse } from "../../../api/client";
import { type PaginationOptions } from '../../../api/pagination'
import {
  fetchGuestRequest,
  fetchGuestRequests,
  searchGuestRequests
} from "../api/guestRequestApi";
import { type GuestRequest } from '../utils/types'
import {
  DetailQueryParameters,
  GenericFilterType,
  type PaginatedQueryParameters
} from "../../../api/hooks/utils/types"
import { guestRequestKeys } from "../utils/queryKeys"
import { SelectOption } from "../../../utils/types"
import { useSmartDetailQuery, useSmartListQuery } from '../../../api/hooks/useSmartQueries';


/* ------------------------- Functional imports end ------------------------- */
export interface GuestRequestFilters extends GenericFilterType {
  request_number?: string
  name?: string
  email?: string
  company?: string
  phone?: string
  status?: string
  createdAt?: Date
  quantity?: string
  description?: string
  editors?: SelectOption[]
}

/**
 * Hook that provides the available GuestRequest instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which
 *  instance to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters of instances changed.
 *  This is useful when you need to run multiple queries sequentially.
 */
export function useGuestRequests(
  parameters: PaginatedQueryParameters<GuestRequest, GuestRequestFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<GuestRequest>> {
  return useSmartListQuery<GuestRequest, GuestRequestFilters>(
    {
      queryKeyFactory: guestRequestKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => fetchGuestRequests(parameters, ids),
      elasticSearchFn: () => searchGuestRequests(parameters)
    },
    enabled,
    instances,
  )
}

export function useGuestRequest(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<GuestRequest>,
  enabled = true,
): UseQueryResult<DetailResponse<GuestRequest>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: guestRequestKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchGuestRequest(id),
    },
    enabled
  )
}


/**
 * Hook to fetch paginated guest requests
 */
export function useGuestRequestsDeprecated(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<GuestRequest>, Error> {
  return useQuery({
    queryKey: guestRequestKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
      expand: options.expand,
    }),
    queryFn: () => fetchGuestRequests(options),
    enabled,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch a single guest request
 */
export function useGuestRequestDeprecated(
  idOrUrl: string | number | undefined,
  expand?: string[],
  enabled = true
): UseQueryResult<GuestRequest, Error> {
  return useQuery({
    queryKey: guestRequestKeys.detail(typeof idOrUrl === 'number' ? idOrUrl : Number(idOrUrl), { expand }),
    queryFn: () => {
      if (!idOrUrl) {
        throw new Error('Guest request ID or URL is required')
      }
      return fetchGuestRequest(idOrUrl, expand)
    },
    enabled: enabled && idOrUrl !== undefined,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch multiple guest requests based on Elasticsearch search results
 * Loads full guest request data from the database for each provided ID
 *
 * @param guestRequestIds - Array of guest request IDs returned by Elasticsearch
 * @param expand - Fields to expand in the guest request response
 * @param enabled - Whether to enable the underlying queries
 */
export function useGuestRequestsFromSearch(
  guestRequestIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: guestRequestIds.map(id => ({
      queryKey: guestRequestKeys.detail(id, { expand }),
      queryFn: () => fetchGuestRequest(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  const guestRequests = queries
    .filter(query => query.isSuccess && query.data)
    .map(query => query.data as GuestRequest)

  const isLoading = queries.some(query => query.isLoading)
  const isIdle = queries.every(query => query.status === 'pending')
  const isDone = queries.every(query => query.status !== 'pending')
  const hasErrors = queries.some(query => query.isError)
  const errors = queries.filter(query => query.isError).map(query => query.error)

  const totalQueries = guestRequestIds.length
  const completedQueries = queries.filter(query => query.status !== 'pending').length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    guestRequests,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries,
  }
}
